import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MoreVertical, Pencil, ArrowUp, ArrowDown, Trash2, ClipboardList, ChevronRight, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import Modal from "@/components/Modal";
import Field from "@/components/Field";
import PaymentStatusChip from "@/components/PaymentStatusChip";
import { SupplyClip, MetersClip, REG_CLIP } from "@/components/RegisterIndicators";
import { SortTh, SortButton, sortProjects } from "@/lib/registerSort";
import { fmtAUD, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TYPE_LABELS = { SSH: "SSH Modules", MMC: "MMC Modules" };

const CHIP = `${REG_CLIP} gap-1`; // Pass 1A: uniform Register clip geometry

function LifecycleChip({ p, testId }) {
  if (p.status === "Completed") {
    return <span data-testid={testId} className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`} style={{ letterSpacing: "0.06em" }}>Completed</span>;
  }
  const ml = p.module_landing || {};
  if (ml.landed) {
    return <span data-testid={testId} className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`} style={{ letterSpacing: "0.06em" }}>Landed{ml.landed_date ? ` · ${fmtDate(ml.landed_date)}` : ""}</span>;
  }
  if (ml.expected_date) {
    return <span data-testid={testId} className={`${CHIP} border-sky-200 bg-sky-50 text-sky-700`} style={{ letterSpacing: "0.06em" }}>Scheduled Landing · {fmtDate(ml.expected_date)}</span>;
  }
  return <span data-testid={testId} className={`${CHIP} border-orange-200 bg-orange-50 text-orange-700`} style={{ letterSpacing: "0.06em" }}>Active</span>;
}

function NbnStatusCell({ p }) {
  return (
    <div className="inline-flex flex-row flex-nowrap items-center justify-center gap-1.5 whitespace-nowrap" data-testid={`ledger-nbn-status-${p.project_number}`}>
      {p.nbn_infrastructure_complete
        ? <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`} style={{ letterSpacing: "0.05em" }}>Pit & Pipe</span>
        : <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`} style={{ letterSpacing: "0.05em" }}>Pit & Pipe</span>}
      {p.nbn_application_complete
        ? <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`} style={{ letterSpacing: "0.05em" }}>Application</span>
        : <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`} style={{ letterSpacing: "0.05em" }}>Application</span>}
    </div>
  );
}

function SolarStatusChip({ status, testId }) {
  if (status === "complete") return <span data-testid={testId} className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`} style={{ letterSpacing: "0.06em" }}>Solar · Complete</span>;
  if (status === "pending") return <span data-testid={testId} className={`${CHIP} border-sky-200 bg-sky-50 text-sky-700`} style={{ letterSpacing: "0.06em" }}>Solar · Pending</span>;
  return <span data-testid={testId} className={`${CHIP} border-orange-200 bg-orange-50 text-orange-700`} style={{ letterSpacing: "0.06em" }}>No Solar</span>;
}


const VIEWS = [
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed / Archived" },
  { key: "all", label: "All" },
];

function NewProjectDialog({ type, onClose, onCreated }) {
  const [distributors, setDistributors] = useState([]);
  const [form, setForm] = useState({
    street: "", suburb: "", state: "VIC", postcode: "",
    distributor: "", supply_type: "Underground", dwelling_count: 2,
    quotation_ex_gst: "", quotation_date: "",
    contract_admin: "", contract_admin_email: "", tranche: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const exGst = parseFloat(form.quotation_ex_gst || 0) || 0;
  const gstInclusive = Math.round(exGst * 1.1 * 100) / 100;

  useEffect(() => { api.get("/meta").then((r) => setDistributors(r.data.distributors)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please upload the Quotation PDF — it becomes the official Base Contract quotation (Revision 1)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        project_type: type,
        address: { street: form.street, suburb: form.suburb, state: form.state, postcode: form.postcode },
        distributor: form.distributor || distributors[0] || "Powercor",
        supply_type: type === "MMC" ? "Underground" : form.supply_type,
        dwelling_count: type === "MMC" ? parseInt(form.dwelling_count, 10) : null,
        base_contract_value: gstInclusive,
        quotation_date: form.quotation_date || null,
        contract_admin: form.contract_admin,
        contract_admin_email: form.contract_admin_email,
        tranche: form.tranche ? parseInt(form.tranche, 10) : null,
      });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("base_value", gstInclusive);
      fd.append("quotation_date", form.quotation_date || "");
      await api.post(`/projects/${data.id}/quotations`, fd);
      toast.success(`Project ${data.project_number} created — ${data.label} (Quotation Revision 1 stored)`);
      onCreated(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`New ${type} Project`} onClose={onClose} wide>
      <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 mb-4">
        The project number is assigned automatically (001, 002, 003…) and is never reused. The project is referenced by its number and address.
      </p>
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="new-project-form">
        <Field label="Tranche Allocation Number (optional)">
          <input data-testid="np-tranche" type="number" min="1" step="1" className="bdx-input font-mono-data" value={form.tranche} onChange={set("tranche")} placeholder="e.g. 1" />
        </Field>
        <div className="flex items-end pb-1">
          <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">Tranche 1 displays as <strong>T1-{type}-XXX</strong>. The underlying project number never changes.</p>
        </div>
        <Field label="Street Address *" className="col-span-2"><input data-testid="np-street" className="bdx-input" required value={form.street} onChange={set("street")} placeholder="e.g. 15 Smith Street" /></Field>
        <Field label="Suburb *"><input data-testid="np-suburb" className="bdx-input" required value={form.suburb} onChange={set("suburb")} placeholder="e.g. Ballarat" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="State"><input data-testid="np-state" className="bdx-input" value={form.state} onChange={set("state")} /></Field>
          <Field label="Postcode"><input data-testid="np-postcode" className="bdx-input" value={form.postcode} onChange={set("postcode")} placeholder="3350" /></Field>
        </div>
        <Field label="Distributor *">
          <select data-testid="np-distributor" className="bdx-input" required value={form.distributor} onChange={set("distributor")}>
            <option value="">Select distributor…</option>
            {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        {type === "SSH" ? (
          <Field label="Supply Type *">
            <select data-testid="np-supply" className="bdx-input" value={form.supply_type} onChange={set("supply_type")}>
              <option>Underground</option>
              <option>Overhead</option>
            </select>
          </Field>
        ) : (
          <Field label="Supply Type">
            <div data-testid="np-supply-fixed" className="bdx-input bg-gray-50 text-gray-600 cursor-not-allowed">Underground — MMC projects are underground only</div>
          </Field>
        )}
        {type === "MMC" ? (
          <Field label="Number of Dwellings *">
            <input data-testid="np-dwellings" type="number" min="1" max="50" className="bdx-input" required value={form.dwelling_count} onChange={set("dwelling_count")} />
            <p className="text-[11px] text-gray-400 mt-1">Unit 1, Unit 2… will be created automatically</p>
          </Field>
        ) : (
          <div className="flex items-end pb-1">
            <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2" data-testid="np-ssh-streams-note">SSH projects automatically include <strong>Main House</strong> and a second stream named from the street address (e.g. <strong>12A Adal Street</strong>); unit, lot or lettered numbers keep <strong>Granny Flat</strong></p>
          </div>
        )}
        <Field label="Contract Administrator (BLG)"><input data-testid="np-contract-admin" className="bdx-input" value={form.contract_admin} onChange={set("contract_admin")} placeholder="e.g. Jane Citizen" /></Field>
        <Field label="Contract Administrator Email"><input data-testid="np-contract-admin-email" type="email" className="bdx-input" value={form.contract_admin_email} onChange={set("contract_admin_email")} placeholder="e.g. jane@betterliving.com.au" /></Field>
        <Field label="Original Quotation Value (Excluding GST) *">
          <input data-testid="np-contract-value" type="number" step="0.01" min="0" required className="bdx-input" value={form.quotation_ex_gst} onChange={set("quotation_ex_gst")} placeholder="e.g. 100000" />
        </Field>
        <Field label="Quotation Date"><input data-testid="np-quotation-date" type="date" className="bdx-input" value={form.quotation_date} onChange={set("quotation_date")} /></Field>
        <div className="col-span-2 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="np-gst-inclusive-strip">
          <div className="flex items-center justify-between">
            <span className="bdx-label text-gray-500">Contract Value (GST Inclusive)</span>
            <span className="font-mono-data text-sm font-semibold text-gray-600" data-testid="np-gst-inclusive-value">{fmtAUD(gstInclusive)}</span>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">Display only — calculated automatically as Quotation Value (ex GST) × 1.1</p>
        </div>
        <Field label="Upload Quotation PDF * (becomes Revision 1 — the official Base Contract quotation)" className="col-span-2">
          <input data-testid="np-quotation-file" type="file" accept="application/pdf,.pdf" className="bdx-input" onChange={(e) => setFile(e.target.files[0])} />
        </Field>
        <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" data-testid="np-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          <button data-testid="np-submit" disabled={busy} className="bdx-btn-primary">{busy ? "Creating…" : "Create Project"}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditProjectDialog({ project, onClose, onSaved }) {
  const [distributors, setDistributors] = useState([]);
  const [form, setForm] = useState({
    street: project.address.street, suburb: project.address.suburb,
    state: project.address.state, postcode: project.address.postcode,
    distributor: project.distributor, supply_type: project.supply_type,
    contract_admin: project.contract_admin || "", contract_admin_email: project.contract_admin_email || "",
    tranche: project.tranche || "",
  });
  const [qFile, setQFile] = useState(null);
  const [qValue, setQValue] = useState(project.base_contract_value);
  const [qDate, setQDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/meta").then((r) => setDistributors(r.data.distributors)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const currentRev = (project.quotations || []).length;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        address: { street: form.street, suburb: form.suburb, state: form.state, postcode: form.postcode },
        distributor: form.distributor,
        supply_type: project.project_type === "MMC" ? "Underground" : form.supply_type,
        contract_admin: form.contract_admin,
        contract_admin_email: form.contract_admin_email,
        tranche: form.tranche ? parseInt(form.tranche, 10) : 0,
      });
      toast.success("Project details saved");
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const uploadRevision = async () => {
    if (!qFile) { toast.error("Select the revised Quotation PDF first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", qFile);
      fd.append("base_value", parseFloat(qValue || 0));
      fd.append("quotation_date", qDate || "");
      const { data } = await api.post(`/projects/${project.id}/quotations`, fd);
      toast.success(`Quotation Revision ${data.revision} uploaded — now Current. Earlier revisions are preserved as Superseded.`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Edit Project ${project.project_number}`} onClose={onClose} wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="edit-project-form">
        <Field label="Street Address" className="col-span-2"><input data-testid="ep-street" className="bdx-input" required value={form.street} onChange={set("street")} /></Field>
        <Field label="Suburb"><input data-testid="ep-suburb" className="bdx-input" value={form.suburb} onChange={set("suburb")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="State"><input data-testid="ep-state" className="bdx-input" value={form.state} onChange={set("state")} /></Field>
          <Field label="Postcode"><input data-testid="ep-postcode" className="bdx-input" value={form.postcode} onChange={set("postcode")} /></Field>
        </div>
        <Field label="Distributor">
          <select data-testid="ep-distributor" className="bdx-input" value={form.distributor} onChange={set("distributor")}>
            {distributors.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        {project.project_type === "SSH" ? (
          <Field label="Supply Type">
            <select data-testid="ep-supply" className="bdx-input" value={form.supply_type} onChange={set("supply_type")}>
              <option>Underground</option><option>Overhead</option>
            </select>
          </Field>
        ) : (
          <Field label="Supply Type">
            <div className="bdx-input bg-gray-50 text-gray-600 cursor-not-allowed">Underground — MMC projects are underground only</div>
          </Field>
        )}
        <Field label="Contract Administrator (BLG)"><input data-testid="ep-contract-admin" className="bdx-input" value={form.contract_admin} onChange={set("contract_admin")} /></Field>
        <Field label="Contract Administrator Email"><input data-testid="ep-contract-admin-email" type="email" className="bdx-input" value={form.contract_admin_email} onChange={set("contract_admin_email")} /></Field>
        <Field label="Tranche Allocation Number (blank = no tranche)">
          <input data-testid="ep-tranche" type="number" min="1" step="1" className="bdx-input font-mono-data" value={form.tranche} onChange={set("tranche")} placeholder="e.g. 1" />
          <p className="text-[11px] text-gray-400 mt-1">Display prefix only — the underlying project number {project.project_type}-{project.project_number} never changes</p>
        </Field>
        <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" data-testid="ep-close" className="bdx-btn-secondary" onClick={onClose}>Close</button>
          <button data-testid="ep-save" disabled={busy} className="bdx-btn-primary">{busy ? "Saving…" : "Save Details"}</button>
        </div>
      </form>

      <div className="mt-5 pt-4 border-t border-gray-200">
        <h4 className="font-heading font-bold text-sm text-gray-900">New Quotation Revision</h4>
        <p className="text-[11px] text-gray-500 mt-1 mb-3">
          Current: {currentRev > 0 ? `Revision ${currentRev}` : "No quotation uploaded"} · Base Contract Value {fmtAUD(project.base_contract_value)} GST Inc.
          Uploading a revision supersedes (never deletes) earlier revisions and updates the Base Contract Value.
        </p>
        <div className="grid grid-cols-3 gap-3 items-end">
          <Field label="Revised Quotation PDF"><input data-testid="ep-quotation-file" type="file" accept="application/pdf,.pdf" className="bdx-input" onChange={(e) => setQFile(e.target.files[0])} /></Field>
          <Field label="New Base Contract Value (GST Inc)"><input data-testid="ep-quotation-value" type="number" step="0.01" min="0" className="bdx-input font-mono-data" value={qValue} onChange={(e) => setQValue(e.target.value)} /></Field>
          <Field label="Quotation Date"><input data-testid="ep-quotation-date" type="date" className="bdx-input" value={qDate} onChange={(e) => setQDate(e.target.value)} /></Field>
        </div>
        <button type="button" data-testid="ep-quotation-upload" disabled={busy} className="bdx-btn-secondary mt-3" onClick={uploadRevision}>Upload Revision {currentRev + 1}</button>
      </div>
    </Modal>
  );
}

function RowMenu({ project, view, onEdit, onMoved, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef();
  const btnRef = useRef();

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("scroll", onScroll, true); };
  }, []);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const menuH = view === "active" ? 170 : 96;
      const top = r.bottom + menuH > window.innerHeight ? Math.max(8, r.top - menuH - 4) : r.bottom + 4;
      setPos({ top, left: Math.max(8, r.right - 208) });
    }
    setOpen(!open);
  };

  const move = async (direction) => {
    setOpen(false);
    try {
      await api.post(`/projects/${project.id}/move`, { direction });
      onMoved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} data-testid={`row-menu-${project.project_number}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-gray-400 transition-[color,background-color,border-color] duration-150 hover:border-[#d9cde5] hover:bg-[#f3eff7] hover:text-[#604184]" onClick={toggle} aria-label={`Project ${project.project_number} actions`}>
        <MoreVertical size={16} strokeWidth={1.5} />
      </button>
      {open && (
        <div data-testid={`row-actions-${project.project_number}`} style={{ position: "fixed", top: pos.top, left: pos.left }} className="z-50 w-52 rounded-md border border-gray-200 bg-white py-1.5 shadow-[0_16px_40px_rgba(16,19,21,0.16)]">
          <button data-testid={`row-edit-${project.project_number}`} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-950" onClick={() => { setOpen(false); onEdit(); }}>
            <Pencil size={13} strokeWidth={1.5} /> Edit Project
          </button>
          {view === "active" && (
            <>
              <button data-testid={`row-move-up-${project.project_number}`} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-950" onClick={() => move("up")}>
                <ArrowUp size={13} strokeWidth={1.5} /> Move Up
              </button>
              <button data-testid={`row-move-down-${project.project_number}`} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-950" onClick={() => move("down")}>
                <ArrowDown size={13} strokeWidth={1.5} /> Move Down
              </button>
            </>
          )}
          <div className="my-1 border-t border-gray-200" />
          <button data-testid={`row-delete-${project.project_number}`} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-red-700 transition-colors duration-150 hover:bg-red-50" onClick={() => { setOpen(false); onDeleted(); }}>
            <Trash2 size={13} strokeWidth={1.5} /> Move to Recycle Bin
          </button>
        </div>
      )}
    </div>
  );
}

export default function LedgerPage({ type }) {
  const { readOnly } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [view, setView] = useState("active");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [tranche, setTranche] = useState("");
  const [tranches, setTranches] = useState([]);
  const [sort, setSort] = useState(null); // null = accepted default ordering; client-side only, survives filter changes
  const [summary, setSummary] = useState(null); // v1.0.18 whole-active-stream financial summary (independent of filters)
  // v1.0.19 WS2 — temporary Admin-only selection (component state only; keyed by stable project id; never persisted)
  const [selected, setSelected] = useState(() => new Map());
  const rows = useMemo(() => sortProjects(projects, sort), [projects, sort]);
  const isActiveRow = (p) => p.status !== "Completed" && !p.archived;
  const snapshot = (p) => ({ id: p.id, project_number: p.project_number, current_contract_sum: p.current_contract_sum || 0,
    claims_raised_total: p.claims_raised_total || 0, claim_payments_outstanding: p.claim_payments_outstanding || 0 });
  const toggleSelect = (p) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(p.id)) next.delete(p.id); else if (isActiveRow(p)) next.set(p.id, snapshot(p));
    return next;
  });
  const clearSelection = () => setSelected(new Map());
  useEffect(() => { // refresh selected snapshots from the latest batched register data; drop rows that are no longer active
    if (!projects || selected.size === 0) return;
    setSelected((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const p of projects) {
        if (!next.has(p.id)) continue;
        if (!isActiveRow(p)) { next.delete(p.id); changed = true; continue; }
        const s = snapshot(p); const o = next.get(p.id);
        if (s.current_contract_sum !== o.current_contract_sum || s.claims_raised_total !== o.claims_raised_total || s.claim_payments_outstanding !== o.claim_payments_outstanding) { next.set(p.id, s); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [projects]); // eslint-disable-line
  const selectedSummary = useMemo(() => {
    if (selected.size === 0) return null;
    const r2 = (v) => Math.round(v * 100) / 100;
    let contract = 0, raised = 0, outstanding = 0;
    for (const s of selected.values()) { contract += s.current_contract_sum; raised += s.claims_raised_total; outstanding += s.claim_payments_outstanding; }
    return { active_total_contract: r2(contract), remaining_value_to_claim: r2(contract - raised), claim_payments_outstanding: r2(outstanding) };
  }, [selected]);
  const shownSummary = selectedSummary || summary;

  const loadTranches = () => api.get(`/tranches?project_type=${type}`).then((r) => setTranches(r.data));
  const loadSummary = () => api.get("/dashboard").then((r) => setSummary(r.data[type]));
  const load = (v = view, t = tranche) =>
    api.get(t ? `/projects?project_type=${type}&view=all&tranche=${t}` : `/projects?project_type=${type}&view=${v}`).then((r) => setProjects(r.data));
  useEffect(() => { setProjects(null); load(view, tranche); loadTranches(); }, [type, view, tranche]); // eslint-disable-line
  useEffect(() => { setSummary(null); loadSummary(); setSelected(new Map()); }, [type]); // eslint-disable-line

  const confirmDelete = async () => {
    try {
      await api.delete(`/projects/${deleting.id}`);
      toast.success(`Project ${deleting.project_number} moved to recycle bin (restorable from Settings)`);
      setDeleting(null);
      load();
      loadTranches();
      loadSummary();
    } catch (e) { toast.error(apiError(e)); }
  };

  const money = (v) => (
    <span className="whitespace-nowrap">{fmtAUD(v || 0)}<span className="ml-0.5 align-baseline text-[0.62em] font-semibold text-gray-500">incl</span></span>
  );

  return (
    <div data-testid={`ledger-${type.toLowerCase()}`} className="bdx-page-enter">
      <Breadcrumbs items={[{ label: "Dashboard", to: "/" }, { label: TYPE_LABELS[type] }]} />
      <div className="flex flex-col gap-5 border-b border-gray-300 pb-7 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.15em" }}><ClipboardList size={15} strokeWidth={1.9} /> BDX Project Register</div>
          <h1 className="font-heading text-3xl font-bold text-gray-950 sm:text-4xl">{TYPE_LABELS[type]}</h1>
          <p className="mt-2 text-sm text-gray-500">{type === "SSH" ? "Social Supported Housing — Main House + Granny Flat projects" : "Modern Methods of Construction — multi-unit projects (Underground only)"}</p>
        </div>
        <div className="flex flex-col items-start gap-5 lg:items-end">
          {!readOnly && (
            <button data-testid="new-project-btn" className="bdx-btn-primary" onClick={() => setShowNew(true)}>
              <Plus size={16} strokeWidth={2} /> New {type} Project
            </button>
          )}
          <div className="flex flex-wrap gap-x-8 gap-y-3 sm:gap-x-10" data-testid="stream-financial-summary" data-mode={selectedSummary ? "selected" : "stream"}
            title={selectedSummary ? `${selected.size} selected active ${type} project${selected.size > 1 ? "s" : ""} — temporary view, nothing is saved` : `Whole active ${type} stream — independent of the register filters below`}>
            {[
              ["Active Contract Value", "active_total_contract", "text-gray-950", "stream-active-contract-value"],
              ["Remaining Value to Claim", "remaining_value_to_claim", "text-[#604184]", "stream-remaining-to-claim"],
              ["Claim Payments Outstanding", "claim_payments_outstanding", "text-red-700", "stream-claims-outstanding"],
            ].map(([label, key, cls, tid]) => (
              <div key={key} className="min-w-[150px] lg:text-right">
                <div className="bdx-label">{label}</div>
                <div data-testid={tid} className={`mt-0.5 font-mono-data text-sm font-semibold ${cls}`}>{shownSummary ? money(shownSummary[key]) : <span className="text-gray-300">—</span>}</div>
              </div>
            ))}
          </div>
          {selectedSummary ? (
            <div className="-mt-3 flex flex-wrap items-center gap-3" data-testid="register-selection-bar">
              <span data-testid="register-selection-count" className="inline-flex items-center rounded-full border border-[#a98cc4] bg-[#f3eff7] px-2.5 py-1 text-[10px] font-bold uppercase text-[#604184]" style={{ letterSpacing: "0.08em" }}>
                Selected projects ({selected.size}) · Incl. GST
              </span>
              <button type="button" data-testid="register-selection-clear" className="text-[10px] font-semibold uppercase text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline" style={{ letterSpacing: "0.06em" }} onClick={clearSelection}>Clear selection</button>
            </div>
          ) : (
            <div className="-mt-3 text-[10px] font-medium uppercase text-gray-400" style={{ letterSpacing: "0.06em" }} data-testid="stream-summary-caption">All values Incl. GST · Active {type} projects · Includes approved variations</div>
          )}
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-300 bg-[#ebeae8] p-1" role="tablist" aria-label="Ledger views">
          {VIEWS.map((v) => (
            <button key={v.key} data-testid={`ledger-view-${v.key}`} onClick={() => { setTranche(""); setView(v.key); }}
              role="tab" aria-selected={view === v.key && !tranche}
              className={`rounded px-4 py-2 text-sm transition-[color,background-color,box-shadow] duration-150 ${
                view === v.key && !tranche ? "bg-white font-semibold text-[#604184] shadow-sm ring-1 ring-[#d9cde5]" : "text-gray-500 hover:bg-white/60 hover:text-gray-900"}`}>
              {v.label}
            </button>
          ))}
        </div>
        {tranches.length > 0 && (
          <select data-testid="ledger-tranche-selector" value={tranche} onChange={(e) => setTranche(e.target.value)}
            className={`h-10 rounded-lg border bg-white px-3 text-sm transition-colors ${tranche ? "border-[#a98cc4] font-semibold text-[#604184] ring-1 ring-[#d9cde5]" : "border-gray-300 text-gray-600"}`}>
            <option value="">All Tranches</option>
            {tranches.map((t) => <option key={t} value={t}>Tranche {t}</option>)}
          </select>
        )}
        {tranche && (
          <span data-testid="ledger-tranche-note" className="text-[11px] text-gray-500">
            Showing all Tranche {tranche} projects — active and completed / archived
          </span>
        )}
      </div>

      <div className="mt-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <div className="flex items-center justify-between border-b border-gray-200 bg-[#faf9f8] px-5 py-4">
          <div>
            <h2 className="font-heading text-base font-bold text-gray-950">Project Register</h2>
            <p data-testid="ledger-record-count" className="mt-0.5 text-xs text-gray-500">{projects === null ? "Loading records" : `${projects.length} ${projects.length === 1 ? "project" : "projects"} in this view`}</p>
          </div>
          <div className="flex items-center gap-6">
            {view === "completed" && projects?.length > 0 && (
              <div className="text-right" data-testid="completed-value-summary">
                <div className="bdx-label">Completed Project Value (GST Inc)</div>
                <div className="mt-0.5 font-mono-data text-sm font-semibold text-gray-950" data-testid="completed-value-total">{fmtAUD(projects.reduce((s, p) => s + (p.current_contract_sum || 0), 0))}</div>
              </div>
            )}
            <div className="bdx-label hidden sm:block">Click any row to open</div>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="bdx-table" data-testid="ledger-table">
          <thead>
            <tr>
              <th className="whitespace-nowrap" data-testid="project-no-header" aria-sort={sort && (sort.key === "project_no" || sort.key === "quotation_date") ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                <div className="flex flex-col items-start gap-0.5">
                  <SortButton col="project_no" label="Project No." sort={sort} onSort={setSort} />
                  <SortButton col="quotation_date" label="Quotation Date" sort={sort} onSort={setSort} size="sm" />
                </div>
              </th>
              <SortTh col="address" label="Address" sort={sort} onSort={setSort} />
              <SortTh col="status" label="Status" sort={sort} onSort={setSort} />
              <SortTh col="meters" label="Meters Connected" sort={sort} onSort={setSort} className="text-center" />
              <SortTh col="supply" label="Distributor / Supply Type" sort={sort} onSort={setSort} className="text-center"><div className="leading-tight" data-testid="supply-header">Distributor<br /><span className="font-normal opacity-70">Supply Type</span></div></SortTh>
              <SortTh col="nbn" label="NBN Status" sort={sort} onSort={setSort} className="text-center"><div className="w-full text-center" data-testid="nbn-status-header">NBN Status</div></SortTh>
              <SortTh col="solar" label="Solar" sort={sort} onSort={setSort} />
              <SortTh col="payment" label="Payment Status" sort={sort} onSort={setSort} />
              <SortTh col="contract_sum" label="Contract Sum (GST Inc)" sort={sort} onSort={setSort} className="text-right" />
              {!readOnly && <th className="w-12 !px-1.5"></th>}
              {!readOnly && <th className="w-10 !px-1.5 text-center" data-testid="register-select-header" aria-label="Select for financial summary" title="Select projects for a temporary financial summary (Admin only)"><CheckSquare size={14} strokeWidth={1.7} className="mx-auto text-gray-500" aria-hidden="true" /></th>}
            </tr>
          </thead>
          <tbody>
            {projects === null && <tr><td colSpan={12} className="py-14 text-center text-gray-400">Loading…</td></tr>}
            {projects?.length === 0 && <tr><td colSpan={12} className="py-16 text-center text-gray-400">No {view === "active" ? "active " : view === "completed" ? "completed / archived " : ""}{type} projects</td></tr>}
            {rows?.map((p) => (
              <tr key={p.id}
                data-testid={`ledger-row-${p.project_number}`}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="group cursor-pointer">
                <td className="whitespace-nowrap !py-3 align-middle" data-testid={`ledger-number-${p.project_number}`}>
                  <div className="font-semibold leading-tight text-gray-950">{p.display_reference || `${p.project_type}-${p.project_number}`}</div>
                  {p.quotation_date && <div className="mt-0.5 font-mono-data text-[11px] font-normal leading-tight text-gray-500" data-testid={`ledger-quotation-date-${p.project_number}`}>{fmtDate(p.quotation_date)}</div>}
                </td>
                <td>
                  <div className="flex min-w-[220px] items-center justify-between gap-4">
                    <span className="font-semibold text-gray-950">{p.label}</span>
                    <ChevronRight size={15} strokeWidth={1.5} className="shrink-0 text-gray-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-gray-700" />
                  </div>
                </td>
                <td><LifecycleChip p={p} testId={`ledger-status-${p.project_number}`} /></td>
                <td className="text-center"><MetersClip p={p} /></td>
                <td className="text-center"><SupplyClip p={p} /></td>
                <td className="text-center"><NbnStatusCell p={p} /></td>
                <td><SolarStatusChip status={p.solar_status} testId={`ledger-solar-status-${p.project_number}`} /></td>
                <td><PaymentStatusChip status={p.payment_status} testId={`ledger-payment-status-${p.project_number}`} variant="register" /></td>
                <td className="text-right font-mono-data font-semibold text-gray-950" data-testid={`ledger-contract-sum-${p.project_number}`}>{fmtAUD(p.current_contract_sum)}</td>
                {!readOnly && (
                  <td className="!px-1.5">
                    <RowMenu project={p} view={tranche ? "all" : view}
                      onEdit={() => setEditing(p)}
                      onMoved={() => load()}
                      onDeleted={() => setDeleting(p)} />
                  </td>
                )}
                {!readOnly && (
                  <td className="!px-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" data-testid={`register-select-${p.project_number}`}
                      className="h-4 w-4 cursor-pointer align-middle accent-[#604184] disabled:cursor-not-allowed disabled:opacity-40"
                      checked={selected.has(p.id)} disabled={!isActiveRow(p)}
                      aria-label={isActiveRow(p) ? `Include project ${p.project_number} in the financial summary` : `Project ${p.project_number} is completed / archived and cannot be included in active totals`}
                      title={isActiveRow(p) ? "Include in financial summary (temporary — nothing is saved)" : "Completed / Archived — not part of active financial totals"}
                      onChange={() => toggleSelect(p)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-gray-500">Contract Sum = Original Base Contract + Approved Variations · Click a row to open the project{view === "active" && !readOnly ? " · Use the row menu to reorder active projects (project numbers never change)" : ""}</p>

      {showNew && <NewProjectDialog type={type} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); loadTranches(); loadSummary(); }} />}
      {editing && <EditProjectDialog project={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); loadTranches(); loadSummary(); }} />}
      {deleting && (
        <Modal title="Move project to recycle bin?" onClose={() => setDeleting(null)}>
          <p className="text-sm text-gray-600">
            Project {deleting.project_type} {deleting.project_number} — {deleting.label} will be moved to the recycle bin.
            It can be restored from Settings. No data will be permanently destroyed.
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button data-testid="row-delete-cancel" className="bdx-btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
            <button data-testid="row-delete-confirm" className="bdx-btn-danger" onClick={confirmDelete}>Move to Recycle Bin</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
